'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { HelpCircle } from 'lucide-react';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { useProjectGoalsGuide } from '@/features/project/hooks/use-goals-guide';
import { useGoalsEditor } from '@/features/project/hooks/use-goals-editor';
import { GoalsBriefGuide } from '@/features/project/ui/GoalsBriefGuide';
import { GoalsCommitButton } from '@/features/project/ui/GoalsCommitButton';
import { GoalsQualityIndicator } from '@/features/project/ui/GoalsQualityIndicator';
import { GoalsEvaluationFeedback } from '@/features/project/ui/GoalsEvaluationFeedback';
import { FacadeAreaField } from '@/features/project/ui/FacadeAreaField';

interface ProjectGoalsSheetProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * NestedSheet with the project goals editor (goals + construction region +
 * facade area). Shared by the render-settings goals row and the estimate
 * editor's facade-area link. Mirrors Apple's `ProjectGoalsView` sheet and
 * Android's `ProjectGoalsPanelContent` bottom sheet.
 *
 * Since the goals-dialogue pivot, edits are committed explicitly via the teal
 * checkmark (top-right) instead of autosaving per keystroke — the commit fires
 * the server-side LLM evaluation. Closing with unsaved edits asks the user
 * whether to save or discard. Buffer/dirty/commit logic lives in
 * `useGoalsEditor`.
 */
export function ProjectGoalsSheet({
  projectId,
  open,
  onOpenChange,
}: ProjectGoalsSheetProps) {
  const t = useTranslations('projectGoals');
  const tProject = useTranslations('project');
  const { data: goalsGuide } = useProjectGoalsGuide();

  const {
    project,
    goals,
    setGoals,
    region,
    setRegion,
    area,
    setArea,
    unit,
    changeUnit,
    areaChanged,
    isDirty,
    commit,
    discard,
  } = useGoalsEditor(projectId);

  const [confirmOpen, setConfirmOpen] = useState(false);
  // "How to write a great brief" guide modal — opened from the "?" next to the
  // brief heading (it also carries the "write in your native language" note).
  const [briefGuideOpen, setBriefGuideOpen] = useState(false);
  // Whether the model's brief-feedback card (the tips bubble) is expanded.
  // Toggled by tapping the quality indicator next to the heading. Expanded by
  // default — mirrors the standalone goals page.
  const [briefFeedbackExpanded, setBriefFeedbackExpanded] = useState(true);

  const score = project?.attributes.goals_quality_score ?? null;
  const evaluation = project?.attributes.goals_evaluation ?? null;
  const pending = !!project?.attributes.is_goals_evaluation_pending;
  const hasGoals = goals.trim().length > 0;
  // The model's evaluation card renders only when there's something to say (or
  // it's in flight), directly under the heading.
  const hasFeedback = pending || (evaluation?.suggestions?.length ?? 0) > 0;

  const requestClose = () => {
    if (isDirty) {
      setConfirmOpen(true);
      return;
    }
    onOpenChange(false);
  };

  return (
    <>
      <NestedSheet
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            requestClose();
            return;
          }
          onOpenChange(true);
        }}
      >
        <NestedSheetContent>
          <NestedSheetHeader
            trailingAction={
              <GoalsCommitButton
                projectId={projectId}
                isDirty={isDirty}
                onCommit={commit}
              />
            }
          >
            <NestedSheetTitle>{t('label')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <div className="space-y-6 py-2">
              {/* Heading + goals textarea — the "write in your native language"
                  note lives in the brief guide opened from the "?" icon. */}
              <div className="space-y-2">
                {/* pb-3: small breathing room below the section heading. */}
                <div className="flex items-center gap-2 pb-3">
                  <Label
                    htmlFor="render-goals"
                    className="text-card-title text-foreground"
                  >
                    {tProject('briefHeading')}
                  </Label>
                  {goalsGuide && (
                    <button
                      type="button"
                      onClick={() => setBriefGuideOpen(true)}
                      aria-label={goalsGuide.guide.trigger}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  )}
                  {/* Goals-quality signal, inline next to the heading (moved off
                      the sheet header so it reads against the brief it describes).
                      Tapping it collapses/expands the model's tips card under the
                      brief input. */}
                  <button
                    type="button"
                    onClick={() => setBriefFeedbackExpanded((v) => !v)}
                    aria-label={tProject('briefTipsToggle')}
                    aria-expanded={briefFeedbackExpanded}
                    // Fixed w-5 footprint so the center is stable across the
                    // empty-dot / spinner / bars states.
                    className="ms-auto flex w-5 justify-center"
                  >
                    <GoalsQualityIndicator
                      score={score}
                      hasGoals={hasGoals}
                      pending={pending}
                      size="sm"
                    />
                  </button>
                </div>
                <Textarea
                  id="render-goals"
                  value={goals}
                  onChange={(e) => setGoals(e.target.value)}
                  placeholder={tProject('projectGoalsPlaceholder')}
                  rows={3}
                  maxLength={10000}
                  className="min-h-[100px] resize-none"
                  // Clarity opt-in: the brief is user-authored design intent,
                  // not PII — unmask it in session replays (Clarity masks all
                  // inputs by default; the dashboard toggle can't override it).
                  data-clarity-unmask="true"
                />
                {/* The model's evaluation reflects the whole brief — text AND
                    photos — shown as neutral "direct speech" directly under the
                    brief input it comments on; collapsible via the quality
                    indicator next to the heading. */}
                {hasFeedback && briefFeedbackExpanded && (
                  <GoalsEvaluationFeedback
                    evaluation={evaluation}
                    pending={pending}
                    score={score}
                  />
                )}
              </div>

              {/* Construction Region field with label + help popover */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="render-region">
                    {tProject('constructionRegion')}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <HelpCircle className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="start">
                      <p className="text-form-help text-muted-foreground">
                        {tProject('constructionRegionHelpText')}
                      </p>
                    </PopoverContent>
                  </Popover>
                </div>
                <Input
                  id="render-region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  placeholder={tProject('constructionRegionPlaceholder')}
                  maxLength={255}
                  // Clarity opt-in: construction region is a place name, not
                  // PII — unmask it in session replays.
                  data-clarity-unmask="true"
                />
              </div>

              {/* Facade area — optional; estimates use it as the
                  authoritative building area instead of re-guessing. */}
              <FacadeAreaField
                id="render-facade-area"
                value={area}
                unit={unit}
                isEstimated={
                  !!project?.attributes.facade_area_is_estimated && !areaChanged
                }
                onChange={setArea}
                onUnitChange={changeUnit}
              />
            </div>
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>

      {/* "How to write a great brief" guide modal — opened from the "?" next to
          the brief heading; carries the native-language note. */}
      {goalsGuide && (
        <GoalsBriefGuide
          guide={goalsGuide.guide}
          note={tProject('goalsLanguageNoticeMessage')}
          open={briefGuideOpen}
          onOpenChange={setBriefGuideOpen}
        />
      )}

      {/* Unsaved-changes guard on close. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tProject('goalsUnsavedTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tProject('goalsUnsavedMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                discard();
                setConfirmOpen(false);
                onOpenChange(false);
              }}
            >
              {tProject('goalsUnsavedDiscard')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                commit();
                setConfirmOpen(false);
                onOpenChange(false);
              }}
            >
              {tProject('goalsUnsavedSave')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
